import { LitElement, html, css, nothing } from 'lit';

customElements.define(
  'katapult-collapse',
  class extends LitElement {
    static properties = {
      _animate: { state: true },
      _contentSize: { state: true },
      horizontal: { type: Boolean },
      opened: { type: Boolean, reflect: true }
    };

    static styles = css`
      :host {
        max-height: -webkit-fill-available;
        max-width: -webkit-fill-available;
        justify-content: center;
        align-items: center;
      }

      :host(:not([opened])) #content {
        pointer-events: none;
      }

      #content {
        /* TODO: This doesn't work on content that has variable width (like text, it always gets squished as much as possible) */
        height: fit-content;
        width: fit-content;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    `;

    render() {
      return html`
        <div id="content">
          <slot></slot>
        </div>
      `;
    }

    get contentSize() {
      return this._contentSize;
    }

    constructor() {
      super();

      // Set default properties.
      this._animate = false;
      this.opened = false;
      this._contentSize = { height: 0, width: 0 };
    }

    connectedCallback() {
      super.connectedCallback();

      // Setup resize observer and callback.
      this._resizeObserver ??= new ResizeObserver((entries) => {
        for (const entry of entries) {
          const contentBoxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;
          if (entry.target == this._content) {
            this._contentSize = { height: contentBoxSize.blockSize, width: contentBoxSize.inlineSize };
          }
        }
      });
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      if (this._animateTimeout) clearTimeout(this._animateTimeout);

      // Disconnect resize observer.
      this._resizeObserver?.disconnect();
      this._resizeObserver = null;
    }

    firstUpdated() {
      // Find the content div.
      this._content = this.renderRoot.querySelector('#content');
    }

    willUpdate(changedProperties) {
      if (changedProperties.has('opened')) {
        // Update animation flag.
        if (this._firstUpdateComplete) {
          if (this._animateTimeout) window.clearTimeout(this._animateTimeout);
          this._animateTimeout = setTimeout(() => (this._animate = false), 300);
          this._animate = true;
        }

        // Update opacity.
        this.style.opacity = this.opened ? 1 : 0;
      }

      // Update transition.
      if (changedProperties.has('_animate')) {
        this.style.transition = this._animate ? 'height 0.3s, width 0.3s, opacity 0.3s' : '';
      }
    }

    updated(changedProperties) {
      if (changedProperties.has('opened')) {
        // Observer/unobserve content.
        this.opened ? this._resizeObserver?.observe(this._content) : this._resizeObserver?.unobserve(this._content);
      }

      if (changedProperties.has('_contentSize')) {
        this.dispatchEvent(new CustomEvent('content-size-change', { detail: this._contentSize }));
      }

      // Update height and width.
      if (['_contentSize', 'opened', 'horizontal'].some((x) => changedProperties.has(x))) {
        if (this.horizontal) {
          this.style.height = '';
          this.style.width = `${this.opened ? this._contentSize.width : 0}px`;
        } else {
          this.style.height = `${this.opened ? this._contentSize.height : 0}px`;
          this.style.width = '';
        }
      }

      // Set first update complete flag.
      if (!this._firstUpdateComplete) this._firstUpdateComplete = true;
    }
  }
);
